import React, { Component } from "react";
import client from "@culturehq/client";
import { Button, Grid, Icon, Info, Loader, Panel, Warning } from "@culturehq/components";

import { useProfile } from "app/context";
import { t, Trans } from "app/utils";

import PageHeader from "components/display/PageHeader";
import SurveyItemList from "components/survey/SurveyItemList";
import SurveyResults from "components/survey/SurveyResults";
import Header from "components/survey/SurveyEditHeader";
import SurveyEditModal from "components/survey/SurveyEditModal";

import styles from "./event-survey.module.scss";

const NavTabs = ({ current, onClick }) => (
  <ul className="nav nav-tabs">
    <li role="presentation">
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <a
        className={current === "edit" ? "active" : "inactive"}
        onClick={() => onClick("edit")}
      >
        <Trans>Edit</Trans>
      </a>
    </li>
    <li role="presentation">
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <a
        className={current === "results" ? "active" : "inactive"}
        onClick={() => onClick("results")}
      >
        <Trans>Results</Trans>
      </a>
    </li>
  </ul>
);

const EmptyResults = ({ event, onEventUpdate }) => (
  <Panel>
    <Panel.Heading primary>
      <Trans>Survey</Trans>
    </Panel.Heading>
    <Panel.Body>
      <div className="CreateSurvey">
        <SurveyEditModal event={event} onEventUpdate={onEventUpdate}>
          {onTrigger => (
            <Button icon="plus" primary onClick={onTrigger}>
              <Trans>Add a Post-Event Survey</Trans>
            </Button>
          )}
        </SurveyEditModal>
      </div>
      <PageHeader title={t("Collect Feedback from Guests")}>
        <Trans>
          Add a survey to your event. Guests will be prompted to provide their
          feedback after the event has ended.
        </Trans>
      </PageHeader>
    </Panel.Body>
  </Panel>
);

const EventSurveyResults = ({ attended, event, survey }) => {
  const { profile } = useProfile();

  if (event && event.hostOnlySurvey && event.host.id !== profile.id) {
    return (
      <Warning>
        <Icon className={styles.warning} icon="android-lock" />
        <Trans>The results of this survey are only visible to the host.</Trans>
      </Warning>
    );
  }

  return <SurveyResults overallResponseCount={attended} survey={survey} />;
};

class EventSurvey extends Component {
  constructor(props) {
    super(props);

    const { event } = props;

    this.state = {
      surveyId: null,
      survey: null,
      loading: true,
      view: event.isPast() ? "results" : "edit"
    };
  }

  componentDidMount = () => {
    this.componentIsMounted = true;
    this.fetchSurvey();
  };

  componentDidUpdate(prevProps, prevState) {
    const { surveyId } = this.state;

    if (prevState.surveyId !== surveyId) {
      this.fetchSurvey();
    }
  }

  componentWillUnmount = () => {
    this.componentIsMounted = false;
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const { surveyId } = nextProps.event;

    if (surveyId !== prevState.surveyId) {
      return { surveyId };
    }
    return null;
  }

  fetchSurvey = async () => {
    const { surveyId } = this.state;

    if (!surveyId) {
      this.setState({ loading: false, survey: null });
      return;
    }

    this.setState({ loading: true });

    const { survey } = await client.getSurvey({ surveyId });

    this.setState({ survey, loading: false });
  };

  handleSurveyCreated = survey => {
    const { onEventUpdate } = this.props;

    onEventUpdate({ surveyId: survey.id });
  };

  handleSurveyEdited = survey => {
    this.setState({ survey });
  };

  handleSurveyDeleted = () => {
    const { onEventUpdate } = this.props;

    onEventUpdate({ surveyId: null });
  };

  handleClick = view => {
    this.setState({ view });
  };

  render() {
    const { event, onEventUpdate } = this.props;
    const { survey, view, loading } = this.state;

    const attended = (
      event && event.rsvpBreakdown && event.rsvpBreakdown.attended
        ? event.rsvpBreakdown.attended
        : null
    );

    return (
      <div className="panel panel-default box-shadow">
        {event.cancelledAt && (
          <Warning>
            <Trans>This event has been cancelled.</Trans>
          </Warning>
        )}

        {event.isPast() && !event.cancelledAt && (
          <Info>
            <Trans>This event has ended.</Trans>
          </Info>
        )}
        <div className="Panel__Body">
          {survey && (
            <NavTabs
              event={event}
              current={view}
              onClick={this.handleClick}
            />
          )}

          <Loader loading={loading}>
            {!survey && (
              <EmptyResults
                event={event}
                onEventUpdate={onEventUpdate}
              />
            )}
            {survey && (
              <>
                <Header survey={survey} event={event} />
                {view === "edit" && (
                  <div className="view padding-15px">
                    <SurveyItemList
                      survey={survey}
                      event={event}
                      onSurveyEdited={this.handleSurveyEdited}
                      onSurveyDeleted={this.handleSurveyDeleted}
                    />
                  </div>
                )}
                {view === "results" && (
                  <div className="view">
                    <Grid className="Event__Analytics">
                      <Grid.Item xs={6} sm={3} className="NumberBlock">
                        <h3><Trans>Total Invited Guests</Trans></h3>
                        <h2>{event.rsvpBreakdown.all}</h2>
                      </Grid.Item>
                      <Grid.Item xs={6} sm={3} className="NumberBlock">
                        <h3><Trans>Confirmed &quot;YES&quot;</Trans></h3>
                        <h2>{event.rsvpBreakdown.attended}</h2>
                      </Grid.Item>
                      <Grid.Item xs={6} sm={3} className="NumberBlock">
                        <h3><Trans>Confirmed &quot;NO&quot;</Trans></h3>
                        <h2>{event.rsvpBreakdown.didNotAttend}</h2>
                      </Grid.Item>
                      <Grid.Item xs={6} sm={3} className="NumberBlock">
                        <h3><Trans>Unconfirmed</Trans></h3>
                        <h2>{event.rsvpBreakdown.unconfirmed}</h2>
                      </Grid.Item>
                    </Grid>
                    {survey && (
                      <div className="padding-15px">
                        <EventSurveyResults attended={attended} event={event} survey={survey} />
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </Loader>
        </div>
      </div>
    );
  }
}

export default EventSurvey;
